import { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { useRenderGate } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'
import { buildBpmSvgForExport } from '@aytm/app/stats/page/question-views/bpm/lib/buildBpmSvgForExport'

import Chart from '@aytm/app/stats/page/question-views/bpm/components/tv/components/chart/Chart'

// The thermometer surface, given the block's view. An export clones the drawn svg: the pins, flags
// and legend are drawn into the DOM, and it carries its own fonts because the clone leaves the page
// stylesheet behind.
const TvChart = ({
  id,
  questionKey,
  questionVariants,
  batches,
  answer,
  colorTheme,
  chartLegendMode,
  chartMode,
  features,
  defaultValues,
  tvPlotByValue,
  tvElevationsRepresentValue,
  tvBrandsValue,
  tvAttributesValue,
  onChartReady
}) => {
  const containerRef = useRef(null)
  const [drawn, setDrawn] = useState(false)

  const chartRendered = useRenderGate()
  const onComplete = useCallback(() => {
    setDrawn(true)
    chartRendered()
  }, [chartRendered])

  useExportableChart({
    deps: [drawn],
    prepareForExport: async () => {
      const svgNode = containerRef.current?.querySelector(`#chart_question_${id} svg`)
      const svgString = buildBpmSvgForExport(svgNode, 't')

      return [await svgToPngDataUrl(svgString, svgNode.width.baseVal.value, svgNode.height.baseVal.value)]
    }
  })

  return (
    <Chart
      ref={containerRef}
      questionId={id}
      questionKey={questionKey}
      questionVariants={questionVariants}
      batches={batches}
      answer={answer}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      chartMode={chartMode}
      tvPlotByValue={tvPlotByValue}
      tvElevationsRepresentValue={tvElevationsRepresentValue}
      tvBrandsValue={tvBrandsValue}
      tvAttributesValue={tvAttributesValue}
      defaultValues={defaultValues.t}
      features={features}
      onChartReady={onChartReady}
      onComplete={onComplete}
    />
  )
}

TvChart.displayName = 'BpmTvChart'

TvChart.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  questionVariants: PropTypes.array,
  batches: PropTypes.array,
  answer: answerPropType,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  chartMode: PropTypes.string,
  features: PropTypes.object,
  defaultValues: PropTypes.object,
  tvPlotByValue: PropTypes.string,
  tvElevationsRepresentValue: PropTypes.string,
  tvBrandsValue: PropTypes.array,
  tvAttributesValue: PropTypes.array,
  onChartReady: PropTypes.func
}

export default TvChart
